import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { comparisonService } from '@/services/comparisonService'
import type {
  ComparisonSubject,
  ComparisonResult,
  ComparisonApiResponse,
} from '@/types/comparison'

/**
 * Hook for comparing two subjects (A vs B).
 *
 * Uses mutation pattern for on-demand comparison execution.
 *
 * @param options - Optional configuration
 * @returns React Query mutation result
 *
 * @example
 * ```tsx
 * const { mutate, data, isLoading, error } = useCompareSubjects()
 *
 * const handleCompare = () => {
 *   mutate({
 *     subjectA: { type: 'platform', id: 1 }, // Instagram
 *     subjectB: { type: 'platform', id: 2 }, // X
 *   })
 * }
 *
 * return (
 *   <div>
 *     <button onClick={handleCompare} disabled={isLoading}>
 *       Compare
 *     </button>
 *     {data && <ComparisonResults result={data.data} />}
 *   </div>
 * )
 * ```
 */
export function useCompareSubjects(options?: {
  /**
   * Callback on success.
   */
  onSuccess?: (data: ComparisonApiResponse) => void

  /**
   * Callback on error.
   */
  onError?: (error: Error) => void

  /**
   * Callback on settled (success or error).
   */
  onSettled?: () => void
}) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      subjectA: ComparisonSubject
      subjectB: ComparisonSubject
    }) => {
      return await comparisonService.compare(request)
    },
    onSuccess: (data) => {
      // Cache the result for future use
      const cacheKey = buildComparisonCacheKey(data.data)
      queryClient.setQueryData(['comparison', cacheKey], data)

      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  })
}

/**
 * Hook for comparing subjects with auto-fetching.
 *
 * Uses query pattern - automatically fetches when subjects are provided.
 *
 * @param subjectA - Subject A to compare
 * @param subjectB - Subject B to compare
 * @param options - Optional configuration
 * @returns React Query result with comparison data
 *
 * @example
 * ```tsx
 * const [subjectA, setSubjectA] = useState<ComparisonSubject | null>(null)
 * const [subjectB, setSubjectB] = useState<ComparisonSubject | null>(null)
 *
 * const { data, isLoading } = useCompareSubjectsQuery(subjectA, subjectB, {
 *   enabled: !!(subjectA && subjectB),
 * })
 *
 * // Automatically fetches when both subjects are selected
 * ```
 */
export function useCompareSubjectsQuery(
  subjectA: ComparisonSubject | null,
  subjectB: ComparisonSubject | null,
  options?: {
    /**
     * Enable/disable the query (default: true if both subjects exist).
     */
    enabled?: boolean

    /**
     * Stale time in milliseconds (default: 5 minutes).
     */
    staleTime?: number

    /**
     * Callback on success.
     */
    onSuccess?: (data: ComparisonApiResponse) => void

    /**
     * Callback on error.
     */
    onError?: (error: Error) => void
  }
) {
  const {
    enabled = !!(subjectA && subjectB),
    staleTime = 5 * 60 * 1000,
    onSuccess,
    onError,
  } = options || {}

  // Build stable cache key
  const cacheKey =
    subjectA && subjectB
      ? buildSubjectsCacheKey(subjectA, subjectB)
      : 'no-subjects'

  return useQuery({
    queryKey: ['comparison', cacheKey],
    queryFn: async () => {
      if (!subjectA || !subjectB) {
        throw new Error('Both subjects are required for comparison')
      }

      return await comparisonService.compare({ subjectA, subjectB })
    },
    enabled,
    staleTime,
    retry: 2,
    retryDelay: 1000,
  })
}

/**
 * Helper: Build cache key from comparison result.
 *
 * @param result - Comparison result
 * @returns Cache key string
 */
function buildComparisonCacheKey(result: ComparisonResult): string {
  const subjectA = result.subjectA
  const subjectB = result.subjectB

  return buildSubjectsCacheKey(
    { type: subjectA.name as any, id: subjectA.id },
    { type: subjectB.name as any, id: subjectB.id }
  )
}

/**
 * Helper: Build cache key from subjects.
 *
 * @param subjectA - Subject A
 * @param subjectB - Subject B
 * @returns Cache key string
 */
function buildSubjectsCacheKey(
  subjectA: ComparisonSubject,
  subjectB: ComparisonSubject
): string {
  const idA = Array.isArray(subjectA.id)
    ? subjectA.id.sort().join(',')
    : String(subjectA.id)
  const idB = Array.isArray(subjectB.id)
    ? subjectB.id.sort().join(',')
    : String(subjectB.id)

  return `${subjectA.type}-${idA}__${subjectB.type}-${idB}`
}

/**
 * Helper: Extract comparison result from query response.
 *
 * @param data - Query response data
 * @returns Comparison result, or null if no data
 */
export function getComparisonResult(
  data: ComparisonApiResponse | undefined
): ComparisonResult | null {
  return data?.data ?? null
}

/**
 * Helper: Check if two subjects are ready for comparison.
 *
 * @param subjectA - Subject A
 * @param subjectB - Subject B
 * @returns True if both subjects are valid and ready
 */
export function canCompareSubjects(
  subjectA: ComparisonSubject | null,
  subjectB: ComparisonSubject | null
): boolean {
  if (!subjectA || !subjectB) return false

  // Check that both subjects have valid type and id
  return !!(
    subjectA.type &&
    subjectA.id !== null &&
    subjectA.id !== undefined &&
    subjectB.type &&
    subjectB.id !== null &&
    subjectB.id !== undefined
  )
}

/**
 * Helper: Format query time for display.
 *
 * @param queryTimeMs - Query time in milliseconds
 * @returns Formatted string (e.g., "1.23s", "456ms")
 */
export function formatQueryTime(queryTimeMs: number | undefined): string {
  if (queryTimeMs === undefined) return 'N/A'

  if (queryTimeMs < 1000) {
    return `${Math.round(queryTimeMs)}ms`
  }

  return `${(queryTimeMs / 1000).toFixed(2)}s`
}
